"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.STABLECOIN_SYMBOLS = void 0;
exports.isAtomicAmount = isAtomicAmount;
exports.formatAtomic = formatAtomic;
exports.groupThousands = groupThousands;
exports.formatAmount = formatAmount;
exports.toAtomic = toAtomic;
exports.sumStablecoinUsd = sumStablecoinUsd;
const ATOMIC_PATTERN = /^\d+$/;
/** True when `value` is an unsigned integer string, the only shape amounts take in CCex. */
function isAtomicAmount(value) {
    return ATOMIC_PATTERN.test(value);
}
/**
 * Converts an atomic-unit string to a human-readable decimal string without floats.
 * "1500000" with 6 decimals becomes "1.5"; trailing zeros are trimmed.
 */
function formatAtomic(atomic, decimals) {
    if (!isAtomicAmount(atomic))
        throw new Error(`Expected atomic amount, got "${atomic}"`);
    if (!Number.isInteger(decimals) || decimals < 0)
        throw new Error(`Expected non-negative integer decimals, got ${decimals}`);
    if (decimals === 0)
        return atomic.replace(/^0+(?=\d)/, '');
    const padded = atomic.padStart(decimals + 1, '0');
    const whole = padded.slice(0, -decimals).replace(/^0+(?=\d)/, '');
    const fraction = padded.slice(-decimals).replace(/0+$/, '');
    return fraction ? `${whole}.${fraction}` : whole;
}
/**
 * Thousands separator for every user-facing amount, in every locale and every channel
 * (Mini App, bot, email): a narrow no-break space (U+202F). Exchange-style notation,
 * "1<U+202F>120.197155", reads the same to a `uk` and an `en` user and never collides with a
 * decimal comma; the no-break space keeps a number on one line.
 */
const THOUSANDS_SEPARATOR = '\u202F';
/** "1234567" becomes "1<U+202F>234<U+202F>567"; the input must be a plain integer string. */
function groupThousands(whole) {
    return whole.replace(/\B(?=(\d{3})+(?!\d))/g, THOUSANDS_SEPARATOR);
}
/**
 * Human-readable amount for display: `formatAtomic` with the integer part grouped by
 * `groupThousands`. Use this, not `formatAtomic`, for anything a user reads; `formatAtomic`
 * stays the plain form for API payloads.
 */
function formatAmount(atomic, decimals) {
    const [whole = '0', fraction] = formatAtomic(atomic, decimals).split('.');
    const grouped = groupThousands(whole);
    return fraction ? `${grouped}.${fraction}` : grouped;
}
/** "1.5" with 6 decimals becomes "1500000". Rejects anything that is not a plain decimal. */
function toAtomic(major, decimals) {
    if (!/^\d+(\.\d+)?$/.test(major))
        throw new Error(`Expected a decimal amount, got "${major}"`);
    if (!Number.isInteger(decimals) || decimals < 0)
        throw new Error(`Expected non-negative integer decimals, got ${decimals}`);
    const [whole, fraction = ''] = major.split('.');
    if (fraction.length > decimals)
        throw new Error(`"${major}" has more precision than ${decimals} decimals`);
    return `${whole}${fraction.padEnd(decimals, '0')}`.replace(/^0+(?=\d)/, '');
}
/** Symbols valued at exactly 1 USD until a rate source exists (spec 2026-09-10, section 5.1). */
exports.STABLECOIN_SYMBOLS = ['USDT', 'USDC'];
/** Scales an atomic amount to whole cents, dropping any remainder below one cent. */
function toCents(atomic, decimals) {
    if (!isAtomicAmount(atomic))
        throw new Error(`Expected atomic amount, got "${atomic}"`);
    if (!Number.isInteger(decimals) || decimals < 0)
        throw new Error(`Expected non-negative integer decimals, got ${decimals}`);
    const value = BigInt(atomic);
    return decimals >= 2 ? value / 10n ** BigInt(decimals - 2) : value * 10n ** BigInt(2 - decimals);
}
/**
 * Sums confirmed USDT and USDC balances as USD 1:1 across chains. Pending amounts and
 * non-stablecoin assets are ignored; symbol matching is case-sensitive because the catalogue
 * always stores upper-case symbols.
 */
function sumStablecoinUsd(chains) {
    let cents = 0n;
    for (const chain of chains) {
        for (const asset of chain.assets) {
            if (!exports.STABLECOIN_SYMBOLS.includes(asset.symbol))
                continue;
            cents += toCents(asset.confirmed, asset.decimals);
        }
    }
    const whole = cents / 100n;
    const fraction = (cents % 100n).toString().padStart(2, '0');
    return { usd: `${whole}.${fraction}` };
}
//# sourceMappingURL=money.js.map